import React from "react";
import type { FunctionComponent } from "react";
import { useEffect, useMemo, useState } from "react";
import {
    ContentWrapper,
    CtaWrapper,
    StyledBox,
    TitleWrapper,
} from "app/pages/website-analysis/components/qualification-sidebar/components/StyledComponents";
import { WidgetTitle } from "app/components/widget-title/WidgetTitle";
import type { ISideBarGenericProps } from "app/pages/website-analysis/components/qualification-sidebar/components/helpers";
import { getSubTitleFilters } from "app/pages/website-analysis/components/qualification-sidebar/components/helpers";
import { i18nFilter } from "app/filters/i18nFilter";
import { abbrNumberVisitsFilter } from "app/filters/ngFilters";
import { DefaultFetchService } from "app/services/fetchService";
import QualificationSideBarContext from "app/components/QualificationSideBar/QualificationSidebarContext";
import DurationService from "app/services/DurationService";
import { NoDataLandscape } from "pro-features/components/NoData/src/NoData";
import { DefaultCellHeader } from "app/components/React/Table/headerCells";
import { CountryCell, TrafficShare } from "app/components/React/Table/cells";
import { colorsPalettes, rgba } from "@similarweb/styles";
import { MiniFlexTable } from "@similarweb/ui-components";
import styled from "styled-components";
import { ComponentsLoader } from "app/pages/website-analysis/components/qualification-sidebar/components/TitleLoader";
import { IconButton } from "@similarweb/ui-components";
import { PlainTooltip } from "@similarweb/ui-components";
import { hasAccessToPackage } from "app/common/services/solutions2Helper";
import { isAvailable } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { allTrackers } from "app/services/track/track";

const StyledTable: any = styled(MiniFlexTable)`
    &.MiniFlexTable {
        padding-bottom: 0;
    }

    .MiniFlexTable-column {
        &:first-child {
            .MiniFlexTable-cell {
                padding-left: 16px;
                padding-right: 16px;
            }
            .MiniFlexTable-headerCell {
                padding-left: 16px;
            }
        }

        &:not(:first-child) {
            .MiniFlexTable-cell {
                padding-right: 16px;
            }
            .MiniFlexTable-headerCell {
                padding-right: 16px;
            }
        }

        .MiniFlexTable-cell {
            height: 40px;
            font-size: 14px;

            &:not(:first-child) {
                border-top: 1px solid ${colorsPalettes.carbon[50]};
            }

            .cell-innerText {
                white-space: nowrap;
            }

            > div {
                height: 100%;
                align-items: center;
                display: flex;
            }
        }
    }

    .MiniFlexTable-headerCell {
        margin-bottom: 2px;
        font-weight: 500;
        font-size: 12px;
        color: ${rgba(colorsPalettes.carbon[500], 0.6)};
    }
`;

const TopCountries: FunctionComponent<ISideBarGenericProps> = ({
    webSource,
    duration,
    country,
    onEmptyData,
    componentId,
}) => {
    const i18n = i18nFilter();
    const { qualificationSideBarSelection } = React.useContext(QualificationSideBarContext);
    const domain = qualificationSideBarSelection?.data?.domain;
    const fetchService = DefaultFetchService.getInstance();
    const durationObject = DurationService.getDurationData(duration, undefined, componentId);
    const { from, to } = durationObject.forAPI;
    const title = i18n("qualification.sidebar.top.countries.title");
    const subTitleFilters = useMemo(
        () => getSubTitleFilters({ webSource, duration, country }),
        [webSource, duration, country],
    );
    const tooltip = i18n("qualification.sidebar.top.countries.title.tooltip");
    const [data, setData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isError, setIsError] = useState<boolean>(false);
    const [totalCount, setTotalCount] = useState<number>(0);
    const isDRIAvailable =
        hasAccessToPackage("digitalsuite") &&
        isAvailable(swSettings.components["digitalsuite_website_marketingchannels"]);

    const getTableColumns = () => {
        return [
            {
                field: "Country",
                displayName: i18n("wwo.geography.table.country.header"),
                headerComponent: DefaultCellHeader,
                cellComponent: CountryCell,
                tooltip: i18n("qualification.sidebar.top.countries.country.tooltip"),
            },
            {
                field: "Share",
                displayName: i18n("traffic.sources.display.ads.publishers.top.categories.share.column.title"),
                headerComponent: DefaultCellHeader,
                cellComponent: TrafficShare,
                tooltip: i18n("qualification.sidebar.top.countries.share.tooltip"),
                width: 180,
            },
        ];
    };

    useEffect(() => {
        async function fetchData() {
            setData(null);
            setIsLoading(true);
            try {
                const response = await fetchService.get<{ Data: object[] }>(
                    "widgetApi/WebsiteGeography/Geography/Table",
                    {
                        country,
                        from,
                        includeSubDomains: true,
                        isWindow: false,
                        keys: domain,
                        timeGranularity: "Monthly",
                        to,
                        webSource,
                        pageSize: 5,
                        orderBy: "TotalShare desc",
                    },
                );
                setData(response["Data"]);
                setTotalCount(response["TotalCount"]);
            } catch (e) {
                setIsError(true);
            } finally {
                setIsLoading(false);
            }
        }
        fetchData();
    }, [country, fetchService, from, to, webSource, domain]);

    const checkIfNoData = () => {
        const isNoData = !data || isError || (Array.isArray(data) && data.length === 0) || totalCount === 0;
        if (isNoData) {
            onEmptyData("qualification_top_countries");
        }
        return isNoData;
    };

    const getTitle = () => (
        <TitleWrapper>
            <WidgetTitle
                title={title}
                tooltip={tooltip}
                filters={subTitleFilters}
            />
        </TitleWrapper>
    );

    const onCtaClick = () => {
        allTrackers.dispatchTrackEvent("Internal Link", "click", "QualificationSidebar/geos/see_more");
    };

    const getContent = () => {
        const ctaHref = proNavigator.href(
            "digitalsuite_website_geography",
            {
                key: domain,
                country,
                duration,
                isWWW: "*",
                webSource,
            },
            {
                absolute: true,
            },
        );
        return checkIfNoData() ? (
            <ContentWrapper height="250px">
                <NoDataLandscape
                    title="qualification.sidebar.top.countries.no-data.title"
                    subtitle="qualification.sidebar.top.countries.no-data.subtitle"
                />
            </ContentWrapper>
        ) : (
            <>
                <ContentWrapper>
                    <StyledTable
                        className="MiniFlexTable"
                        data={data}
                        columns={getTableColumns()}
                    />
                </ContentWrapper>
                <CtaWrapper className={"submit-button"}>
                    {isDRIAvailable && (
                        <PlainTooltip
                            maxWidth={220}
                            tooltipContent={i18n("qualification.sidebar.top.countries.cta.tooltip")}
                        >
                            <a
                                href={ctaHref}
                                target="_blank"
                                rel="noreferrer"
                                onClick={onCtaClick}
                            >
                                <IconButton
                                    type="outlined"
                                    iconName="country-usage-rank"
                                    iconSize="xs"
                                >
                                    {i18n("qualification.sidebar.top.countries.cta", {
                                        count: abbrNumberVisitsFilter()(totalCount),
                                    })}
                                </IconButton>
                            </a>
                        </PlainTooltip>
                    )}
                </CtaWrapper>
            </>
        );
    };

    return (
        <StyledBox
            maxHeight="430px"
            data-automation="qualification-sidebar-top-countries"
        >
            {isLoading ? (
                <ComponentsLoader />
            ) : (
                <>
                    {getTitle()} {getContent()}
                </>
            )}
        </StyledBox>
    );
};
export const MemoizedTopCountries = React.memo(TopCountries);
